import { cn } from '@vidahealth/vida-ui';
import { type ReactNode } from 'react';
import { Pressable, StyleProp, ViewStyle } from 'react-native';

type SelectableRowProps = {
  className?: string;
  style?: StyleProp<ViewStyle>;
  isSelected: boolean;
  onPress: () => void;
  /** Row content — e.g. a checkbox + label, or an avatar + label. */
  children: ReactNode;
  /**
   * Bordered row (default) vs. a borderless plain row — e.g. a trailing
   * "None of these" option reads as a separate, plainer choice.
   */
  bordered?: boolean;
  /**
   * Selection semantics for assistive tech: 'checkbox' (multi-select, default)
   * or 'radio' (single-select). Controls both the a11y role and which a11y
   * state flag carries `isSelected`.
   */
  selectionRole?: 'checkbox' | 'radio';
  /** Screen-reader label; pass it since `children` may not be a plain string. */
  accessibilityLabel?: string;
  testID?: string;
};

// A full-row pressable option matching the vida-design-system onboarding row
// pattern — the whole row is the tap target. Content-agnostic: callers pass
// whatever goes inside (a checkbox + label for health conditions, an avatar +
// label for health goals, …). The row owns the shell + press + selection a11y
// and reflects selection with a highlighted (primary, 2px) border; richer
// per-item visuals (a checkbox, etc.) can additionally live in the children.
export function SelectableRow({
  className,
  style,
  isSelected,
  onPress,
  children,
  bordered = true,
  selectionRole = 'checkbox',
  accessibilityLabel,
  testID,
}: SelectableRowProps) {
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole={selectionRole}
      accessibilityState={
        selectionRole === 'radio' ? { selected: isSelected } : { checked: isSelected }
      }
      accessibilityLabel={accessibilityLabel}
      className={cn(
        // 1px resting outline, 2px primary when selected. `py-4` (no fixed
        // height) lets rows with taller content grow to fit.
        'box-border flex-row items-center gap-4 rounded-sm px-6 py-4',
        bordered && (isSelected ? 'border-2 border-primary-500' : 'border border-outline-200'),
        className
      )}
      style={style}
    >
      {children}
    </Pressable>
  );
}
